import { z } from "zod";

import { dateSchema } from "./utils";

const decimalString = z.coerce
  .number()
  .transform((number) => number.toFixed(2));

const iconColor = z.object({
  base: z.string(),
  dark_background: z.string(),
  light_background: z.string(),
});

const predictionContractSchema = z
  .object({
    affiliate_url: z.string().url().optional().catch(undefined),
    id: z.string(),
    title: z.string(),
    event_id: z.string(),
    yes: decimalString.nullable(),
    no: decimalString.nullable(),
    status: z.string().nullable(),
    chance: decimalString.nullable(),
    thumbnail: z.string().optional(),
    thumbnail_static: z.string().optional(),
    icon_abbrev: z.string().nullable(),
    icon_colors: z.array(iconColor).catch([]),
    type: z.string().nullable().catch(null),
  })
  .transform((data) => {
    return {
      ...data,
      yes: data.yes ? Number(data.yes).toFixed(2) : null,
      no: data.no ? Number(data.no).toFixed(2) : null,
    };
  });

const predictionSchema = z.object({
  id: z.string(),
  title: z.string(),
  event_date: dateSchema,
  type: z.string().nullable().catch(null),
  status: z.string().optional().catch(undefined),
  closed_date: dateSchema.nullable().catch(null),
  category: z.string(),
  subcategory_display_name: z.string().nullable().catch(null),
  contracts: z.array(predictionContractSchema),
  venue: z.string().optional().catch(undefined),
  thumbnail: z.string().optional().catch(undefined),
  thumbnail_static: z.string().optional().catch(undefined),
});

const predictionGroupSchema = z.object({
  id: z.string(),
  display_name: z.string(),
  avatar: z.string(),
  avatar_static: z.string(),
  header_static: z.string(),
  url: z.string().url(),
  group_visibility: z.string(),
  members_count: z.number(),
  slug: z.string(),
});

const predictionChartSchema = z.object({
  name: z.string(),
  colors: z.object({
    light: z.string().nullable(),
    dark: z.string().nullable(),
  }),
  datapoints: z.array(z.array(z.number())),
});

const basePredictionGameSchema = z.object({
  type: z.string(),
});

const teamSchema = z.object({
  name: z.string().catch(""),
  score: z.number().catch(0),
  icon_colors: z.array(iconColor).catch([]),
  abbreviation: z.string().catch(""),
});

const groupSportGameSchema = basePredictionGameSchema.extend({
  type: z.string(),
  data: z.object({
    is_live: z.boolean().catch(false),
    is_over: z.boolean().catch(false),
    time: z.string().nullable().catch(null),
    period: z.string().nullable().catch(null),
    away_team: teamSchema,
    home_team: teamSchema,
  }),
});

const gameNBASchema = groupSportGameSchema.extend({
  type: z.literal("nba"),
});

const gameNHLSchema = groupSportGameSchema.extend({
  type: z.literal("nhl"),
});

const gameMLSSchema = groupSportGameSchema.extend({
  type: z.literal("mls"),
});

const gameMLBSchema = groupSportGameSchema.extend({
  type: z.literal("mlb"),
});

const gameNFLSchema = groupSportGameSchema.extend({
  type: z.literal("nfl"),
});

const gameCBBSchema = groupSportGameSchema.extend({
  type: z.literal("cbb"),
});

const gameCWBBSchema = groupSportGameSchema.extend({
  type: z.literal("cwbb"),
});

const gameEPLSchema = groupSportGameSchema.extend({
  type: z.literal("soccer"),
});

const gameCFBSchema = groupSportGameSchema.extend({
  type: z.literal("cfb"),
});

const gameGolfSchema = basePredictionGameSchema.extend({
  type: z.literal("golf"),
  data: z.object({
    is_live: z.boolean().catch(false),
    is_over: z.boolean().catch(false),
    round: z.string(),
    leader: z.string(),
    event_name: z.string(),
  }),
});

const gameMMASchema = basePredictionGameSchema.extend({
  type: z.literal("mma"),
  data: z.object({
    is_live: z.boolean().catch(false),
    is_over: z.boolean().catch(false),
    round: z.string(),
    result: z.string(),
    fighter_1: z.object({
      name: z.string(),
      is_winner: z.boolean().nullable(),
      abbreviation: z.string(),
    }),
    fighter_2: z.object({
      name: z.string(),
      is_winner: z.boolean().nullable(),
      abbreviation: z.string(),
    }),
    event_name: z.string(),
  }),
});

const gameSchema = z.discriminatedUnion("type", [
  gameNBASchema,
  gameMLSSchema,
  gameNHLSchema,
  gameMLBSchema,
  gameNFLSchema,
  gameCBBSchema,
  gameCWBBSchema,
  gameEPLSchema,
  gameCFBSchema,
  gameGolfSchema,
  gameMMASchema,
]);

const predictionStatusSchema = z.object({
  id: z.string(),
  uri: z.string(),
  url: z.string().url(),
  replies_count: z.number(),
  reblogs_count: z.number(),
  reblogged: z.boolean(),
  group: predictionGroupSchema,
  sporting_event: gameSchema.nullable().catch(null),
  prediction: predictionSchema,
  watching: z.boolean().catch(false),
});

type PredictionContract = z.infer<typeof predictionContractSchema>;
type Prediction = z.infer<typeof predictionSchema>;
type PredictionStatus = z.infer<typeof predictionStatusSchema>;
type Game = z.infer<typeof gameSchema>;

export {
  predictionSchema,
  predictionStatusSchema,
  predictionContractSchema,
  gameSchema,
  predictionChartSchema,
  type Prediction,
  type PredictionContract,
  type PredictionStatus,
  type Game,
};
